import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { SwivuList } from "@/modern/components/swivu-list";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { useState, useEffect, useMemo } from "react";
import { NOTIFICATIONS_BANNERS_PREF_NAMES, NOTIFY_PREFS_TYPES_TEXT } from "@/other/constants/OtherKeys";
import { Combobox, TComboOption } from "@/modern/components/ui/combobox";
import { Mail, Smartphone, AlertCircle, BellRing } from "lucide-react";

interface NotifyPreference {
  id: string | number;
  notifyType: number;
  isEmailNotify: boolean;
  isWorkforceNotify: boolean;
  interupt: boolean;
  bannerNotify: number;
  order: number;
}

const NotificationDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project } = useSwivuBryntumContext();
  const [notifyPreferences, setNotifyPreferences] = useState<NotifyPreference[]>([]);

  const { subscribe } = useSwivuStore({
    storeId: STORE_KEYS.notifyPreferenceStore,
  });

  const loadNotifyPreferences = () => {
    if (!project) return;
    const notifyPreferenceStore = project.getCrudStore(STORE_KEYS.notifyPreferenceStore);
    const records = (notifyPreferenceStore.query(() => true, true) || []) as unknown as NotifyPreference[];
    setNotifyPreferences(records);
  };

  useEffect(() => {
    loadNotifyPreferences();
    return subscribe(() => {
      loadNotifyPreferences();
    });
  }, [subscribe]);

  const handleReorderItems = (movedItem: NotifyPreference, beforeItem: NotifyPreference | null) => {
    const notifyPreferenceStore = project.getCrudStore(STORE_KEYS.notifyPreferenceStore);
    // @ts-expect-error - store.move is a valid method
    notifyPreferenceStore.move(movedItem, beforeItem);
  };

  // Notification methods options for multi-select
  const notificationMethodsOptions: TComboOption[] = useMemo(() => [
    { label: "Email", value: "email", icon: <Mail className="h-4 w-4 text-blue-500" /> },
    { label: "Workforce App", value: "workforce", icon: <Smartphone className="h-4 w-4 text-green-500" /> },
    { label: "Interrupt", value: "interrupt", icon: <AlertCircle className="h-4 w-4 text-orange-500" /> },
  ], []);

  // Board banners options for single-select
  const boardBannersOptions: TComboOption[] = useMemo(() =>
    NOTIFICATIONS_BANNERS_PREF_NAMES.map(({ id, name }) => ({
      label: name,
      value: id,
      icon: <BellRing className="h-4 w-4 text-purple-500" />
    })),
    []);

  const getNotificationMethodsValue = (notifyPref: NotifyPreference): string[] => {
    const values: string[] = [];
    if (notifyPref.isEmailNotify) values.push("email");
    if (notifyPref.isWorkforceNotify) values.push("workforce");
    if (notifyPref.interupt) values.push("interrupt");
    return values;
  };

  const handleNotificationMethodsChange = (notifyPref: NotifyPreference, newValues: string[] | null) => {
    const values = Array.isArray(newValues) ? newValues : [];
    notifyPref.isEmailNotify = values.includes("email");
    notifyPref.isWorkforceNotify = values.includes("workforce");
    notifyPref.interupt = values.includes("interrupt");
  };

  const handleBoardBannersChange = (notifyPref: NotifyPreference, newValue: number | string | null) => {
    if (newValue !== null) {
      notifyPref.bannerNotify = Number(newValue);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      <div className="max-w-6xl overflow-x-auto">
        <SwivuList.Root<NotifyPreference>
          items={notifyPreferences}
          onReorder={handleReorderItems}
        >
          {/* Column Headers */}
          <div className="flex items-center gap-2 px-2.5 py-2 mb-2 bg-muted/30 rounded-md border border-border/40 min-w-[850px]">
            {/* Drag handle space */}
            <div className="w-8 flex-shrink-0" />

            {/* Notification Type Header */}
            <div className="flex-shrink-0 w-52 min-w-[13rem]">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Notification Type
              </span>
            </div>

            {/* Notification Methods Header */}
            <div className="flex-1 min-w-[320px] px-2">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Notification Methods
                </span>
                <span className="text-[10px] text-muted-foreground/60">(How you receive alerts)</span>
              </div>
            </div>

            {/* Board Banners Header */}
            <div className="flex-1 min-w-[280px] px-2">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Board Banners
                </span>
                <span className="text-[10px] text-muted-foreground/60">(Visibility settings)</span>
              </div>
            </div>
          </div>

          <SwivuList.Content<NotifyPreference> emptyMessage="No notification preferences found." className="min-w-max">
            {(notifyPref: NotifyPreference) => (
              <SwivuList.Item value={notifyPref.id as string | number}>
                {/* Drag Handle */}
                <SwivuList.ItemHandle value={notifyPref.id as string | number} />

                {/* Notify Type Label - Read Only */}
                <div className="flex-shrink-0 w-52 min-w-[13rem] px-2">
                  <span className="text-sm font-medium text-foreground">
                    {NOTIFY_PREFS_TYPES_TEXT[notifyPref.notifyType]}
                  </span>
                </div>

                {/* Notification Methods - Multi-select with badges */}
                <div className="flex-1 min-w-max px-2">
                  <Combobox
                    options={notificationMethodsOptions}
                    value={getNotificationMethodsValue(notifyPref)}
                    onValueChange={(value) => handleNotificationMethodsChange(notifyPref, value as string[])}
                    placeholder="Select methods"
                    multiSelect={true}
                    showBadges={true}
                    enableRemoveButton={true}
                    size="sm"
                  />
                </div>

                {/* Board Banners - Single-select with badges */}
                <div className="min-w-[280px] pr-2 pl-12">
                  <Combobox
                    options={boardBannersOptions}
                    value={notifyPref.bannerNotify}
                    onValueChange={(value) => handleBoardBannersChange(notifyPref, value as number)}
                    placeholder="Select banner visibility"
                    multiSelect={false}
                    showBadges={true}
                    size="sm"
                  />
                </div>
              </SwivuList.Item>
            )}
          </SwivuList.Content>
        </SwivuList.Root>
      </div>
    </div>
  );
};

export default NotificationDetail;
